import { useState, useEffect, useCallback } from 'react';
import { ZOOM_RANGE, DEFAULT_PIXELS_PER_BEAT } from '../utils/constants';

export const useZoom = (numberOfBars = 16, beatsPerMeasureMap = []) => {
  // State to track actual viewport width
  const [viewportWidth, setViewportWidth] = useState(1200);

  // Calculate actual total beats in the piece
  const calculateActualTotalBeats = (bars, beatsMap) => {
    if (!beatsMap || beatsMap.length === 0) {
      // Fallback to 4/4 assumption if no beats map provided
      return bars * 4;
    }
    
    // Sum up actual beats from all measures
    let totalBeats = 0;
    for (let i = 0; i < Math.min(bars, beatsMap.length); i++) {
      totalBeats += beatsMap[i] || 4; // Default to 4 beats if undefined
    }
    
    // If we have fewer beats in map than bars, assume 4/4 for remaining
    if (bars > beatsMap.length) {
      totalBeats += (bars - beatsMap.length) * 4;
    }
    
    return totalBeats;
  };

  // Calculate minimum zoom to exactly fill viewport (no empty space)
  const calculateMinZoomForActualBeats = (bars, beatsMap) => {
    const actualTotalBeats = calculateActualTotalBeats(bars, beatsMap);
    const maxBeatsToShow = 400; // Cap at 400 beats
    
    // Use actual beats if ≤400, otherwise cap at 400
    const beatsToShow = Math.min(actualTotalBeats, maxBeatsToShow);
    
    // Calculate annotation area width (84% of viewport, accounting for track labels, borders, and scrollbar)
    const annotationAreaWidth = viewportWidth * 0.83;
    
    // Calculate minimum zoom to exactly fill the available width
    const minZoomForPerfectFit = annotationAreaWidth / (beatsToShow * DEFAULT_PIXELS_PER_BEAT);
    
    // Ensure minimum zoom is not below our absolute minimum
    return Math.max(ZOOM_RANGE.HORIZONTAL.MIN, minZoomForPerfectFit);
  };

  // Effect to detect and update viewport width
  useEffect(() => {
    const updateViewportWidth = () => {
      setViewportWidth(window.innerWidth);
    };

    // Set initial width
    updateViewportWidth();

    // Add resize listener
    window.addEventListener('resize', updateViewportWidth);

    // Cleanup
    return () => window.removeEventListener('resize', updateViewportWidth);
  }, []);

  // Calculate the initial slider value that would give us zoom level of 1.0
  const calculateInitialSliderValue = (bars, beatsMap) => {
    const minZoomValue = calculateMinZoomForActualBeats(bars, beatsMap);
    
    // If we want dynamicZoomValue to be 1.0, we need to solve for horizontalZoomLevel
    // Using the inverse of our zoom calculation formula
    const normalizedValue = Math.pow((1.0 - minZoomValue) / (ZOOM_RANGE.HORIZONTAL.MAX - minZoomValue), 1/2.5);
    
    return normalizedValue * (ZOOM_RANGE.HORIZONTAL.MAX - ZOOM_RANGE.HORIZONTAL.MIN) + ZOOM_RANGE.HORIZONTAL.MIN;
  };

  // Zoom levels
  const [horizontalZoomLevel, setHorizontalZoomLevel] = useState(() => calculateInitialSliderValue(numberOfBars, beatsPerMeasureMap));
  const [verticalZoomLevel, setVerticalZoomLevel] = useState(1);
  const [dynamicZoomValue, setDynamicZoomValue] = useState(1); // Actual zoom value used by components

  // Calculate dynamic zoom level based on slider position and number of bars
  useEffect(() => {
    // Calculate minimum zoom based on actual beats
    const minZoomValue = calculateMinZoomForActualBeats(numberOfBars, beatsPerMeasureMap);
    
    // Map the linear slider value to a non-linear zoom level
    const normalizedSliderPosition = (horizontalZoomLevel - ZOOM_RANGE.HORIZONTAL.MIN) / 
                                    (ZOOM_RANGE.HORIZONTAL.MAX - ZOOM_RANGE.HORIZONTAL.MIN);
    
    if (normalizedSliderPosition <= 0) {
      // At minimum zoom (left-most position) - show all beats (if ≤400) or 400 beats
      setDynamicZoomValue(minZoomValue);
    } else if (normalizedSliderPosition >= 1) {
      // At maximum zoom (right-most position)
      setDynamicZoomValue(ZOOM_RANGE.HORIZONTAL.MAX);
    } else {
      // In between - use exponential scaling for smoother transition
      const expFactor = 2.5; // Controls how quickly zoom transitions from min to max
      const scaledValue = Math.pow(normalizedSliderPosition, expFactor);
      setDynamicZoomValue(minZoomValue + scaledValue * (ZOOM_RANGE.HORIZONTAL.MAX - minZoomValue));
    }
  }, [horizontalZoomLevel, numberOfBars, beatsPerMeasureMap, viewportWidth]);

  const setHorizontalZoomLevelValue = useCallback((value) => {
    setHorizontalZoomLevel(previous => {
      const resolved = typeof value === 'function' ? value(previous) : value;
      return Math.min(Math.max(resolved, ZOOM_RANGE.HORIZONTAL.MIN), ZOOM_RANGE.HORIZONTAL.MAX);
    });
  }, []);

  const setVerticalZoomLevelValue = useCallback((value) => {
    setVerticalZoomLevel(previous => {
      const resolved = typeof value === 'function' ? value(previous) : value;
      return Math.min(Math.max(resolved, ZOOM_RANGE.VERTICAL.MIN), ZOOM_RANGE.VERTICAL.MAX);
    });
  }, []);

  const adjustHorizontalZoom = useCallback((direction) => {
    setHorizontalZoomLevelValue(previous => previous + direction * ZOOM_RANGE.HORIZONTAL.STEP);
  }, [setHorizontalZoomLevelValue]);

  const adjustVerticalZoom = useCallback((direction) => {
    setVerticalZoomLevelValue(previous => previous + direction * ZOOM_RANGE.VERTICAL.STEP);
  }, [setVerticalZoomLevelValue]);

  // Sliders, gestures, wheel input, and shortcuts all share these clamped setters.
  const handleHorizontalZoomChange = useCallback((event) => {
    setHorizontalZoomLevelValue(parseFloat(event.target.value));
  }, [setHorizontalZoomLevelValue]);

  const handleVerticalZoomChange = useCallback((event) => {
    setVerticalZoomLevelValue(parseFloat(event.target.value));
  }, [setVerticalZoomLevelValue]);

  return {
    horizontalZoomLevel, // The raw slider value (0.5 to 4.0)
    dynamicZoomValue,    // The calculated zoom value based on number of bars
    verticalZoomLevel,
    handleHorizontalZoomChange,
    handleVerticalZoomChange,
    setHorizontalZoomLevelValue,
    setVerticalZoomLevelValue,
    adjustHorizontalZoom,
    adjustVerticalZoom,
  };
};
